import { useEffect, useState } from 'react';
import { Check } from './common';
import { FAX_SETUP } from '../eobFixtures';
import { saveFaxSetup } from '../store';

export function PageViewer({ pages, start, onClose }: { pages: number; start: number; onClose: () => void }) {
  const [page, setPage] = useState(start);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="po-lightbox" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-dialog" style={{ maxWidth: 760 }} role="dialog" aria-modal="true" aria-label={`Fax page ${String(page)}`}>
        <div className="po-dialog-actions" style={{ justifyContent: 'space-between', marginTop: 0 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={`po-btn po-btn-sm ${n === page ? 'po-btn-primary' : ''}`} onClick={() => { setPage(n); }}>{n}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="po-btn po-btn-sm" aria-label="Zoom out" onClick={() => { setZoom((z) => Math.max(0.5, z - 0.25)); }}>−</button>
            <button type="button" className="po-btn po-btn-sm" aria-label="Zoom in" onClick={() => { setZoom((z) => Math.min(2, z + 0.25)); }}>+</button>
            <button type="button" className="po-btn po-btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="ph" style={{ width: '100%', height: 420 * zoom, maxHeight: '60vh', marginTop: 10 }}>Fax page {page} (mock)</div>
      </div>
    </div>
  );
}

export function FaxSetupDialog({ onClose }: { onClose: () => void }) {
  const [how, setHow] = useState<'service' | 'inbox'>('service');
  const [senders, setSenders] = useState(FAX_SETUP.senders);
  const [tested, setTested] = useState<'idle' | 'waiting' | 'done'>('idle');
  const [copied, setCopied] = useState(false);
  const valid = senders.includes('@');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const test = (): void => {
    setTested('waiting');
    setTimeout(() => { setTested('done'); }, 3000);
  };
  return (
    <div className="po-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-dialog" role="dialog" aria-modal="true" aria-label="Send your EOB faxes to ELVA">
        <h2 className="po-dialog-title">Send your EOB faxes to ELVA</h2>
        <p className="po-dialog-body">If your faxes arrive as emails, send a copy of each one to this address. ELVA reads every attachment, keeps the EOBs and skips everything else.</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ background: 'var(--bg)', padding: '6px 10px', borderRadius: 8, fontSize: 13 }}>{FAX_SETUP.address}</code>
          <button type="button" className="po-btn po-btn-sm" onClick={() => { try { void navigator.clipboard.writeText(FAX_SETUP.address); } catch { /* mock */ } setCopied(true); }}>Copy</button>
          <span aria-live="polite" className="po-vh">{copied ? 'Address copied' : ''}</span>
        </div>
        <div>
          <span className="po-label">How do your faxes arrive?</span>
          <div className="po-radio-list" role="radiogroup" aria-label="How do your faxes arrive?">
            <label className="po-radio"><input type="radio" name="po-fax-how" checked={how === 'service'} onChange={() => { setHow('service'); }} /><span>From a fax service (RingCentral, eFax, Fax.Plus, …). Add the address above as a second email recipient in its settings.</span></label>
            <label className="po-radio"><input type="radio" name="po-fax-how" checked={how === 'inbox'} onChange={() => { setHow('inbox'); }} /><span>In a shared inbox (Gmail, Outlook). Add a rule that forwards emails from your fax service to the address above.</span></label>
          </div>
        </div>
        <div>
          <label className="po-label" htmlFor="po-senders">Accept faxes from</label>
          <input id="po-senders" className="po-field" value={senders} onChange={(e) => { setSenders(e.target.value); }} />
          <p className="po-meta" style={{ marginTop: 4 }}>ELVA ignores email from any other sender. Add more than one, separated by commas.</p>
        </div>
        <p className="po-notice">Faxes hold patient information. ELVA receives them under your agreement with ELVA, keeps the EOBs with the postings they create, and deletes everything it skips after 30 days.</p>
        {tested !== 'idle' ? (
          <p className="po-meta" role="status">{tested === 'waiting' ? 'Waiting for your test fax…' : <span><span className="po-tick" aria-hidden="true"><Check /></span> Received your test fax at 2:14 PM</span>}</p>
        ) : null}
        <div className="po-dialog-actions">
          <button type="button" className="po-btn" onClick={test}>Send a test fax</button>
          <button type="button" className="po-btn po-btn-primary" disabled={!valid} aria-describedby={valid ? undefined : 'po-fax-reason'} onClick={() => { saveFaxSetup(senders.trim()); onClose(); }}>Done</button>
          {valid ? null : <span id="po-fax-reason" className="po-disabled-reason">Add a sender’s email address first.</span>}
        </div>
      </div>
    </div>
  );
}

export function UploadPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="po-backdrop" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-dialog" style={{ maxWidth: 440, margin: '20px' }} role="dialog" aria-modal="true" aria-label="Uploading EOBs">
        <h2 className="po-dialog-title">3 files uploaded by Ava Thompson</h2>
        <div aria-live="polite">
          <div className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div><strong>Scan_Oct14.pdf · 12 pages</strong><br /><span className="po-meta">3 EOBs found: MetLife $842.60, Cigna $1,096.00, United Concordia $410.00. All in To post.</span></div></div>
          <div className="po-tickrow"><span className="po-tick" aria-hidden="true" style={{ background: 'var(--muted)', color: 'var(--mfg)' }}>…</span><div><strong>IMG_2231.HEIC · 1 page</strong><br /><span className="po-meta">Reading…</span></div></div>
          <div className="po-tickrow"><span className="po-tick" aria-hidden="true" style={{ background: 'var(--wsoft)', color: 'var(--warning)' }}>!</span><div><strong>IMG_2232.HEIC · 1 page</strong><br /><span className="po-meta">Too dark to read the amounts. Take the photo again in better light, flat and from straight above.</span> <button type="button" className="po-src">Choose another file</button></div></div>
        </div>
        <p className="po-meta">You can close this. ELVA keeps reading, and each EOB appears in To post when it’s ready.</p>
        <div className="po-dialog-actions"><button type="button" className="po-btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
